/**
 * 年齢認証オーバーレイ（TK-015、FR-001 / FR-002 / FR-003、ADR-003）。
 *
 * **HTML の初期出力に認証結果による差分を作らない。**オーバーレイはこのモジュール
 * （クライアント JS）が挿入し、サーバ／エッジでの出し分けは一切行わない。JS を実行しない
 * クローラーは結果として認証を経由せず、コンテンツをそのまま取得できる（FR-002）。
 *
 * 遮蔽は「コンテンツへのブラー + オーバーレイの不透明マスク」の二重で行い、`inert` で
 * 操作を無効化する（`04-architecture.md` age-gate）。認証結果は `localStorage` の
 * `zp.age` に保存し（有効期限 90 日）、保存できない環境ではメモリ上のフラグに
 * フォールバックして当該セッション中のみ認証済みとして扱う。エラーは表示しない。
 */

import { clearAge, readVerified, writeVerified } from './age-store';
import { safeStorage } from './storage';

/**
 * 「18歳未満」を選んだときの遷移先。コンテンツを表示せず、外部の一般サイトへ送る
 * （FR-001）。
 */
export const EXIT_URL = 'https://www.yahoo.co.jp/';

/**
 * 画像の出所を明示する掲示。年齢確認パネルと遊び方ページ（`public/rules.html`）で
 * 同じ文言を使う。片方を直したらもう片方と spec（FR-001）も揃えること。
 */
export const AI_IMAGE_DISCLAIMER =
  '画像はテキストプロンプトにより架空の成人を架空の環境に配置したAI生成であり、実在の人物とは関係ありません。';

/** 認証状態を破棄するクエリパラメータ（FR-003）。 */
export const RESET_PARAM = 'reset-age';

/**
 * `localStorage` が使えないセッションでの認証フラグ。
 *
 * プライベートブラウジング等ではモジュールの生存期間 = セッションとみなし、
 * ページ内での再認証を避ける。
 */
let memoryVerified = false;

/**
 * 年齢認証を初期化する。未認証ならオーバーレイを挿入して `content` を遮蔽する。
 *
 * @param content 遮蔽対象（アプリのルート要素）。オーバーレイ自体はその外（body 直下）に置く
 * @param onVerified 認証の成立時に呼ぶ。既に認証済みなら即座に呼ぶ。ゲーム状態機械が
 *   `AGE_GATE` から先へ進む合図に使う（TK-013）
 */
export function initAgeGate(content: HTMLElement, onVerified?: () => void): void {
  const storage = safeStorage();

  // `?reset-age` は認証済みかどうかにかかわらず先に破棄する（FR-003）。
  if (new URLSearchParams(window.location.search).has(RESET_PARAM)) {
    if (storage) clearAge(storage);
    memoryVerified = false;
  }

  const verified = memoryVerified || (storage !== null && readVerified(storage, Date.now()));
  if (verified) {
    onVerified?.();
    return;
  }

  content.inert = true;
  content.classList.add('age-gate-blur');

  const overlay = document.createElement('div');
  overlay.className = 'age-gate';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'age-gate-title');
  overlay.setAttribute('aria-describedby', 'age-gate-message age-gate-disclaimer');

  const panel = document.createElement('div');
  panel.className = 'age-gate-panel';

  const title = document.createElement('h1');
  title.id = 'age-gate-title';
  title.className = 'age-gate-title';
  title.textContent = '年齢確認';

  const message = document.createElement('p');
  message.id = 'age-gate-message';
  message.className = 'age-gate-message';
  message.textContent =
    'このサイトには成人向け（18歳以上）のコンテンツが含まれます。あなたは18歳以上ですか？';

  const disclaimer = document.createElement('p');
  disclaimer.id = 'age-gate-disclaimer';
  disclaimer.className = 'age-gate-disclaimer';
  disclaimer.textContent = AI_IMAGE_DISCLAIMER;

  const enter = document.createElement('button');
  enter.type = 'button';
  enter.className = 'age-gate-enter';
  enter.textContent = '18歳以上です — 入場する';
  enter.addEventListener('click', () => {
    if (storage) writeVerified(storage, Date.now());
    else memoryVerified = true;
    overlay.remove();
    content.inert = false;
    content.classList.remove('age-gate-blur');
    onVerified?.();
  });

  const leave = document.createElement('button');
  leave.type = 'button';
  leave.className = 'age-gate-leave';
  leave.textContent = '18歳未満です — 退出する';
  leave.addEventListener('click', () => {
    window.location.href = EXIT_URL;
  });

  panel.append(title, message, disclaimer, enter, leave);
  overlay.append(panel);
  document.body.append(overlay);

  // ボタンをキーボードで操作可能にする（NFR-006）。button 要素なので Tab / Enter は
  // ネイティブに効く。初期フォーカスを与えてタブ移動の起点を明確にする。
  enter.focus();
}
